import { GameObject, Vector2, Rectangle } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { MushroomField } from '../systems/MushroomField';

export class Flea implements GameObject {
  public id: string;
  public position: Vector2;
  public velocity: Vector2;
  public size: Vector2;
  public active: boolean = true;

  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private speed: number = 120;
  private gameHeight: number;
  private animationTime: number = 0;
  private animationFrame: number = 0;
  private mushroomField: MushroomField | null = null;
  private dropMushroomTimer: number = 0;
  private dropMushroomInterval: number = 0.8; // Drop mushroom every 0.8 seconds

  constructor(
    id: string,
    x: number,
    y: number,
    renderManager: RenderManager,
    audioManager: AudioManager,
    gameHeight: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.velocity = { x: 0, y: this.speed };
    this.size = { x: 16, y: 16 };
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.gameHeight = gameHeight;
  }

  public setMushroomField(mushroomField: MushroomField): void {
    this.mushroomField = mushroomField;
  }

  public update(deltaTime: number): void {
    // Update animation
    this.animationTime += deltaTime;
    if (this.animationTime >= 0.1) {
      this.animationTime = 0;
      this.animationFrame = (this.animationFrame + 1) % 4;
    }
    
    // Update mushroom drop timer
    this.dropMushroomTimer += deltaTime;
    
    // Move down
    this.position.y += this.velocity.y * deltaTime;
    
    // Drop mushrooms as it falls
    if (this.mushroomField && this.dropMushroomTimer >= this.dropMushroomInterval) {
      this.mushroomField.addMushroom(this.position.x, this.position.y);
      this.dropMushroomTimer = 0;
    }
    
    // Remove when off screen
    if (this.position.y > this.gameHeight) {
      this.active = false;
    }
  }

  public render(ctx: CanvasRenderingContext2D): void {
    if (!this.active) return;

    const centerX = this.position.x + this.size.x / 2;
    const centerY = this.position.y + this.size.y / 2;

    // Flea patterns for animation (falling insect)
    let pattern: number[][];
    
    switch (this.animationFrame) {
      case 0:
        pattern = [
          [0,0,1,1,1,1,0,0],
          [0,1,1,0,0,1,1,0],
          [1,1,1,1,1,1,1,1],
          [1,0,1,1,1,1,0,1],
          [0,1,1,1,1,1,1,0],
          [0,0,1,0,0,1,0,0],
          [0,1,0,1,1,0,1,0],
          [1,0,0,0,0,0,0,1]
        ];
        break;
      case 1:
        pattern = [
          [0,0,1,1,1,1,0,0],
          [0,1,0,1,1,0,1,0],
          [1,1,1,1,1,1,1,1],
          [1,1,1,1,1,1,1,1],
          [0,1,1,1,1,1,1,0],
          [0,1,0,1,1,0,1,0],
          [1,0,0,0,0,0,0,1],
          [0,0,1,0,0,1,0,0]
        ];
        break;
      case 2:
        pattern = [
          [0,0,1,1,1,1,0,0],
          [0,1,1,0,0,1,1,0],
          [1,1,1,1,1,1,1,1],
          [1,0,1,1,1,1,0,1],
          [0,1,1,1,1,1,1,0],
          [1,0,0,1,1,0,0,1],
          [0,0,1,0,0,1,0,0],
          [0,1,0,0,0,0,1,0]
        ];
        break;
      case 3:
        pattern = [
          [0,0,1,1,1,1,0,0],
          [0,1,0,1,1,0,1,0],
          [1,1,1,1,1,1,1,1],
          [1,1,1,1,1,1,1,1],
          [0,1,1,1,1,1,1,0],
          [1,0,1,0,0,1,0,1],
          [0,0,0,1,1,0,0,0],
          [0,1,0,0,0,0,1,0]
        ];
        break;
      default:
        pattern = [
          [0,0,1,1,1,1,0,0],
          [0,1,1,1,1,1,1,0],
          [1,1,1,1,1,1,1,1],
          [1,1,1,1,1,1,1,1],
          [0,1,1,1,1,1,1,0],
          [0,1,0,1,1,0,1,0],
          [1,0,0,0,0,0,0,1],
          [0,0,1,0,0,1,0,0]
        ];
    }

    const pixelSize = 2;
    const color = '#FFFF00'; // Yellow flea

    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col]) {
          const x = centerX - (pattern[row].length * pixelSize) / 2 + col * pixelSize;
          const y = centerY - (pattern.length * pixelSize) / 2 + row * pixelSize;
          this.renderManager.fillRect(x, y, pixelSize, pixelSize, color);
        }
      }
    }
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.size.x,
      height: this.size.y
    };
  }

  public destroy(): void {
    this.active = false;
  }

  public getPointValue(): number {
    return 200; // Points for destroying a flea
  }
}